/**
 *  @file       debugging.js
 *              Utility functions for improving debug output, etc, in DCP JavaScript programs.
 *
 *  @author     Wes Garland, wes@kingsds.network
 *  @date       Nov 2019
 */

'use strict';
const env = require('./common/dcp-env');
const { setenv, getenv } = env;
var selfDebugging = false;
var scopeList = [];

var startTime = 0;
var jsonParseTime = 0;
var jsonStringifyTime = 0;

var originalJsonParse = JSON.parse;
var originalJsonStringify = JSON.stringify;

function logJsonStats()
{
  console.debug(`---- JSON Metrics ----`);
  console.debug(`Timespan:       ${(performance.now() - startTime).toFixed(2).padStart(10)}ms`);
  console.debug(`JSON.parse:     ${jsonParseTime.toFixed(2).padStart(10)}ms`);
  console.debug(`JSON.stringify: ${jsonStringifyTime.toFixed(2).padStart(10)}ms`);
}

function hookJsonTimings()
{
  if (!process.env.DCP_DEBUG_JSON)
  {
    if (startTime > 0)
    {
      logJsonStats();
      startTime = 0;
      jsonParseTime = 0;
      jsonStringifyTime = 0;
      JSON.parse = originalJsonParse;
      JSON.stringify = originalJsonStringify;
      process.off('exit', logJsonStats);
    }
  }
  else if (startTime === 0)
  {
    startTime = performance.now();
    JSON.parse = (text, reviver) => {
      var start = performance.now();
      var res = originalJsonParse(text, reviver);
      jsonParseTime += performance.now() - start;
      return res;
    }
    JSON.stringify = (value, replacer, space) => {
      var start = performance.now();
      var res = originalJsonStringify(value, replacer, space);
      jsonStringifyTime += performance.now() - start;
      return res;
    }
    process.on('exit', logJsonStats);
  }
  else
    return; //already running, don't reset it
}

function hookConsoleDebugforTimestamps()
{
  if (hookConsoleDebugforTimestamps.ran)
    return;
  hookConsoleDebugforTimestamps.ran = true;

  const underlying_consoleDebug = console.debug;
  const chalk = require('dcp/utils').chalk();
  function consoleDebugWithTimestamps()
  {
    if (!process.env.DCP_DEBUG_TIMESTAMPS)
      underlying_consoleDebug(...arguments);
    else
    {
      const ts = performance.now().toFixed(1).padStart(5, ' ');
      underlying_consoleDebug(chalk.grey(ts), ...arguments);
    }
  }
  console.debug = consoleDebugWithTimestamps;
}

function logStalledEventLoop()
{
  var now;
  if (!process.env.DCP_DEBUG_LONG_EVENT_LOOP)
    return;
  if (logStalledEventLoop.timer)
    return;

  const reportThreshold = parseInt(process.env.DCP_DEBUG_LONG_EVENT_LOOP, 10) || 100;
  now = performance.now();
  function checkEventLoopTime()
  {
    if (!process.env.DCP_DEBUG_LONG_EVENT_LOOP)
    {
      logStalledEventLoop.timer = undefined;
      return;
    }

    const checkTime = performance.now();
    if (checkTime-now > reportThreshold)
      console.debug(`Warning: excessively long event loop cycle, took ${checkTime-now}ms`);
    now = checkTime;
    logStalledEventLoop.timer = setImmediate(checkEventLoopTime);
  }
  logStalledEventLoop.timer = setImmediate(checkEventLoopTime);
}

/**
 * Declare a new debugging scope.  Each scope can watch a separate watchObject to determine
 * whether or not we are currently in debug mode.  Every scope watches the environment 
 * variable 'DCP_DEBUG'. 
 *
 * The debugging scope is queried by invoking the returned function, which has an optional
 * 'subsystem' argument.  When the query is made without the subsystem argument, the returned
 * function returns true if any subsystem in the scope is being debugged.  Otherwise, we return
 * true when the current debugging operations matches the debugging scope and specified subsystem.
 *
 * The watch object and the DCP_DEBUG environment variable are re-evaluated every time the 
 * returned function is called, however the results of matching for a given scope/subsystem are 
 * cached for speed.
 *
 * @param      {string}     scopeName      The name of the debugging scope (eg. 'compute')
 * @param      {object}     [watchObject={}]    An optional object whose `debug` property is watched
 *                                         to determine what debugging operation we are performing.
 * @returns    {function}   A function which returns boolean which is only true when the current
 *                          debugging operation describes both the current scope and subsystem.
 *
 * @example
 * // logs when watchObject.debug or DCP_DEBUG='compute' or '*'
 * debugging = require('debugging').scope('compute');
 * debugging() && console.log('hello');
 *
 * @example 
 * // logs when watchObject.debug or DCP_DEBUG='compute:for' or '*'
 * debugging = require('debugging').scope('compute');
 * debugging('for') && console.log('hello');
 * 
 * @example
 * // Note that environment variables can be set in local/session storage for a 
 * // browser. The following is equivalent to DCP_DEBUG='dcp:*'
 * localStorage.setItem("env", JSON.stringify({"DCP_DEBUG": "dcp:*"}));
 * // in source code, both of these will log.
 * debugging = require('debugging').scope('dcp');
 * debugging('sender') && console.debug('hello');
 * debugging('receiver') && console.debug('world');
 */
exports.scope = function debugging$$scope(scopeName, watchObject = {})
{
  var debugSyms;
  var disabled=false;

  if (!scopeName)
    throw new Error('Must specify scopeName!');

  selfDebugging && console.log(`debugging ${scopeName} - create scope`);

  function initializeScope()
  {
    const dcpDebug = getenv('DCP_DEBUG');

    debugSyms = (dcpDebug || '') + (dcpDebug && watchObject.debug ? ',' : '') + (watchObject.debug || '');
    debugging.cache = {};
    debugging.cacheValidity = debugSyms;

    selfDebugging && console.log(`debugging ${scopeName} - initialized lookup cache`);
  }

  function debugging(subsystem) {
    var re;

    if (disabled === true)
      return false;

    if (!selfDebugging && debugging.cache.hasOwnProperty(subsystem))
      return debugging.cache[subsystem];

    /* No self-debugging code above to keep cache-hit and watch-object paths fast */
    
    let scopeMatch, subsysMatch;
    loop: for (let debugSym of debugSyms.split(',')) {
      scopeMatch = (debugSym === scopeName || debugSym.startsWith(scopeName + ':'));
      subsysMatch = false || (!subsystem);

      selfDebugging && console.log(`debugging ${scopeName}:${subsysMatch ? '(weak)' : subsystem} - examining debugSym ${debugSym}`);

      if (scopeMatch && subsysMatch) {
        selfDebugging && console.log(`debugging ${scopeName} - early match`);
        break loop;
      }

      if (typeof debugSym === 'boolean' && debugSym) {
        scopeMatch = debugSym;
        selfDebugging && console.log(`debugging ${scopeName} - matched on boolean debugSym`);
        break;
      }

      switch(debugSym)
      {
        case '*':                               
        case 'all':
          selfDebugging && console.log(`debugging ${scopeName} - ${debugSym} matched scope`);
          scopeMatch = scopeName !== 'debugging';
          break loop;
        case '*:*':
        case 'all:*':
        case '*:all':
        case 'all:all':
          selfDebugging && console.log(`debugging ${scopeName} - ${debugSym} matched everything`);
          scopeMatch = subsysMatch = scopeName !== 'debugging';
          break loop;
        case scopeName + ':all' :              /* everything for this scope */
          selfDebugging && console.log(`debugging ${scopeName} - ${debugSym} matched everything for this scope`);
          scopeMatch = subsysMatch = true;
          break loop;
        case scopeName + ':*' :                
        case 'all:' + subsystem:
        case '*:' + subsystem:                 /* everything for this subsystem */
          selfDebugging && console.log(`debugging - ${debugSym} matched everything for this subsystem`);
          scopeMatch = scopeName !== 'debugging';
          subsysMatch = true;
          break loop;
        case scopeName + ':' + subsystem:      /* things for this scope and subsystem */
          selfDebugging && console.log(`debugging - ${debugSym} matched scope and subsystem`);
          scopeMatch = subsysMatch = true;
          break loop;
      }

      re = new RegExp('^(' + debugSym + ')$');
      selfDebugging && console.log(`debugging ${scopeName} - checking regular expression ${re}`);
      if (re.test(scopeName)) {
        selfDebugging && console.log(`debugging ${scopeName} - ${debugSym} matched regular expression for scope`);
        scopeMatch = true;
        break loop;
      }
      else if (re.test(subsystem)) {
        selfDebugging && console.log(`debugging ${scopeName} - ${debugSym} matched regular expression for subsystem`);
        subsysMatch = true;
        break loop;
      }
      else if (re.test(scopeName + ':' + subsystem)) {
        selfDebugging && console.log(`debugging ${scopeName} - ${debugSym} matched regular expression for subsystem`);
        scopeMatch = subsysMatch = true;
        break loop;
      }

      if (scopeMatch && subsysMatch)
        break loop;

      selfDebugging && console.log(`debugging ${scopeName} - ${debugSym} => no match for ${scopeName}:${subsystem}`);
    } /* loop */

    return (debugging.cache[subsystem] = (scopeMatch && subsysMatch));
  }

  if (scopeList.length === 0 && env.platform === 'nodejs')  /* todo - wire up pythonmonkey /wg aug 2024 */
  {
    const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
    const process = requireNative('process');
    // Each time evalBundleFileInIIFE is called on the client bundle we create a brand new global scopeList
    // for debugging.js which causes process event handlers to load on SIGHUP and SIGUSR2 (under node).
    // Normally we call evalBundleFileInIIFE twice on the client bundle, but CI can call dcp/tests/lib/dcp-context.js
    // which does it again and it's easy to reach the default limit of 10 handlers per event. We're doing
    // the easy fix of increaing the event handler limit by 2 every time a new global scopeList is created.
    process.setMaxListeners(process.getMaxListeners() + 2);
    process.on('SIGHUP',  () => exports.reinit());
    process.on('SIGUSR2', () => exports.toggle());
  }

  scopeList.push({
    toggle: ()        => (disabled = !disabled),
    set:    (enabled) => (disabled = !enabled),
    reinit: ()        => initializeScope()
  });

  initializeScope();

  return debugging;
}

/**
 * Re-read and maybe re-specify DCP_DEBUG. This causes all running debugging()
 * scopes in the current program to reinitialize, allowing dynamic changes.
 *
 * @param {string} DCP_DEBUG       if defined, changes the value of DCP_DEBUG environment variable
 */
exports.reinit = function reinit(DCP_DEBUG)
{
  hookConsoleDebugforTimestamps();
  logStalledEventLoop();
  hookJsonTimings();
  if (typeof DCP_DEBUG !== 'undefined')
    setenv('DCP_DEBUG', DCP_DEBUG);

  selfDebugging && console.debug(`Reinitialize debugging scopes; DCP_DEBUG=${getenv('DCP_DEBUG')}`);
  scopeList.forEach(scope => scope.reinit())
}

/** Toggle all scopes on/off */
exports.toggle = function toggle() {
  scopeList.forEach(scope => scope.toggle());
}

/** Set all scopes on/off */
exports.set = function set(enabled) {
  scopeList.forEach(scope => scope.set(enabled));
  if (enabled)
    selfDebugging && console.debug('set debugging enabled');
  else
    selfDebugging && console.debug('set debugging disabled');
}

selfDebugging = exports.scope('debugging')();

/** re-wire console.debug to write to stderr when DCP_DEBUG_STDERR is set */
if (process.env.DCP_DEBUG_STDERR && typeof globalThis.console !== 'undefined' && require('dcp/common/dcp-env').platform === 'nodejs')
{
  const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
  const c = new (requireNative('console').Console)({stdout: process.stderr, stderr: process.stderr});
  globalThis.console.debug = c.debug;
}

logStalledEventLoop();
hookJsonTimings();